import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { CheckCircle2, ChevronRight, Circle, Compass, Rocket, X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEntitlements } from "@/hooks/use-entitlements";
import { apiRequest } from "@/lib/queryClient";

type GettingStartedKey =
  | "padsplit_sync"
  | "property_details"
  | "booking_template"
  | "vendors"
  | "smart_locks"
  | "thermostats"
  | "dynamic_pricing"
  | "cleaning"
  | "knowledge_base"
  | "appliances"
  | "supplies"
  | "furnishings"
  | "buy_box";

interface GettingStartedResponse {
  items: Record<GettingStartedKey, boolean>;
  completed: number;
  total: number;
  percent: number;
  dismissed: boolean;
}

interface ChecklistItem {
  key: GettingStartedKey;
  label: string;
  detail: string;
  href: string;
}

/**
 * Activation checklist, ordered as the fastest path to a running portfolio:
 * PadSplit sync first (it back-fills properties/rooms/members/messages),
 * then the operating basics, then per-property enrichment. Completion flags
 * come live from GET /api/app/getting-started — an item checks itself off
 * however the user gets it done.
 */
const SETUP_ITEMS: ChecklistItem[] = [
  {
    key: "padsplit_sync",
    label: "Connect PadSplit",
    detail: "Syncs in your properties, rooms, members, and messages automatically.",
    href: "/app/padsplit-sync",
  },
  {
    key: "property_details",
    label: "Set up your properties",
    detail: "Add your Wi-Fi, trash pickup day, and other important details to each property.",
    href: "/app/properties",
  },
  {
    key: "booking_template",
    label: "Set up your booking request template",
    detail: "The questions every prospective member answers when they apply.",
    href: "/app/members/booking-requests?tab=templates",
  },
  {
    key: "vendors",
    label: "Set up your Vendor Directory",
    detail: "Add the vendors you dispatch for maintenance, cleaning, and turns.",
    href: "/app/operations/vendors",
  },
  {
    key: "thermostats",
    label: "Connect your thermostats",
    detail: "Monitor temps and apply presets across every property.",
    href: "/app/devices/thermostats",
  },
  {
    key: "smart_locks",
    label: "Connect your smart locks",
    detail: "TTLock, Sifely, and many other smart locks — automatic member door codes.",
    href: "/app/devices/smart-locks",
  },
  {
    key: "dynamic_pricing",
    label: "Create a dynamic pricing model",
    detail: "Rules that keep room rates competitive and push them to PadSplit.",
    href: "/app/pricing/models",
  },
  {
    key: "cleaning",
    label: "Set up your cleaning schedule",
    detail: "Recurring member cleaning schedules with reminders and check-offs.",
    href: "/app/cleaning",
  },
  {
    key: "knowledge_base",
    label: "Add your knowledge base",
    detail: "Company-wide answers here; add per-property notes on each property page.",
    href: "/app/account/knowledge-base",
  },
  {
    key: "appliances",
    label: "Import appliances to properties",
    detail: "Track every appliance per property from its property page.",
    href: "/app/properties",
  },
  {
    key: "supplies",
    label: "Import supplies you reorder",
    detail: "Your restock catalog — items you order again and again.",
    href: "/app/supplies",
  },
  {
    key: "furnishings",
    label: "Import your furnishing catalog",
    detail: "The furniture list you buy for every new property launch.",
    href: "/app/furnishing-catalog",
  },
];

// Legacy localStorage key: if set, migrate the dismissal to the server once
// then clear it so the user doesn't see the checklist pop back.
const LEGACY_DISMISS_KEY = "padpulse-get-started-dismissed";

interface GetStartedLauncherProps {
  sidebarOpen?: boolean;
  isMobile?: boolean;
}

export function GetStartedLauncher({ sidebarOpen = true, isMobile = false }: GetStartedLauncherProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const entitlements = useEntitlements();

  const statusQuery = useQuery<GettingStartedResponse>({
    queryKey: ["/api/app/getting-started"],
    // A failed fetch must never permanently hide the checklist: retry, keep
    // the last-known data across refetches, and (below) render an unchecked
    // list rather than nothing when there's an error with no cached data.
    retry: 2,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  });

  const dismissMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/app/getting-started/dismiss"),
    meta: { silentSave: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["/api/app/getting-started"] });
      setOpen(false);
    },
  });

  // One-time migration: if the old localStorage flag is set, persist the
  // dismissal to the server and remove the local key so teammates on other
  // devices also see the checklist as dismissed.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(LEGACY_DISMISS_KEY) !== "1") return;
    // Only migrate once the query has loaded (avoids a race where we call
    // dismiss before knowing the current server state).
    if (!statusQuery.data) return;
    if (statusQuery.data.dismissed) {
      // Already dismissed server-side — just clean up the stale local key.
      localStorage.removeItem(LEGACY_DISMISS_KEY);
      return;
    }
    localStorage.removeItem(LEGACY_DISMISS_KEY);
    dismissMutation.mutate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.data]);

  const items: Record<GettingStartedKey, boolean> =
    statusQuery.data?.items ?? ({} as Record<GettingStartedKey, boolean>);
  const doneCount = SETUP_ITEMS.filter((i) => items[i.key]).length;
  const percent = Math.round((doneCount / SETUP_ITEMS.length) * 100);

  const operator = entitlements.accountKind !== "property_manager";
  const exploreLinks: Array<{ key: string; label: string; detail: string; href: string; done?: boolean }> = [];
  if (operator && entitlements.has("research")) {
    exploreLinks.push(
      {
        key: "research",
        label: "Market Research",
        detail: "City-level data to pick your next market.",
        href: "/app/research",
      },
      {
        key: "buy_box",
        label: "Set up your Buy Box",
        detail: "Automated property search against your buying criteria.",
        href: "/app/buy-box/settings",
        done: items.buy_box,
      },
    );
  }
  if (operator && entitlements.has("revenue")) {
    exploreLinks.push({
      key: "revenue_analytics",
      label: "Revenue & portfolio analytics",
      detail: "Occupancy, room performance, and your Mercury-connected income.",
      href: "/app/analytics",
    });
  }

  const dismiss = () => {
    dismissMutation.mutate();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`group flex w-full items-center rounded-lg bg-brand-accent px-3 py-2.5 text-left text-brand-accent-foreground shadow-sm transition-colors hover:bg-brand-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${sidebarOpen ? "gap-2" : "justify-center"}`}
          data-testid="button-get-started"
          aria-expanded={open}
          aria-label="Set Up and Grow"
          title={!sidebarOpen ? "Set Up and Grow" : undefined}
        >
          <Rocket size={16} className="shrink-0" />
          {sidebarOpen && (
            <>
              <span className="min-w-0 flex-1 whitespace-nowrap text-sm font-semibold">Set Up and Grow</span>
              <span className="sr-only" data-testid="badge-get-started-count">
                {doneCount}/{SETUP_ITEMS.length}
              </span>
            </>
          )}
          {!sidebarOpen && (
            <span className="sr-only" data-testid="badge-get-started-count">
              {doneCount}/{SETUP_ITEMS.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={isMobile ? "bottom" : "right"}
        align="start"
        sideOffset={12}
        collisionPadding={12}
        className="z-[60] w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border-border/80 bg-background p-0 shadow-xl"
        data-testid="panel-get-started"
      >
          <div className="p-4 pb-3 border-b bg-brand/5">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Rocket size={16} className="text-brand shrink-0" /> Set Up and Grow
              </h3>
              <button
                type="button"
                aria-label="Dismiss the setup checklist"
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                onClick={dismiss}
                disabled={dismissMutation.isPending}
                data-testid="button-get-started-dismiss"
              >
                <X size={16} />
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Progress value={percent} className="h-1.5" />
              <span className="text-xs font-medium text-muted-foreground shrink-0" data-testid="text-get-started-progress">
                {doneCount}/{SETUP_ITEMS.length}
              </span>
            </div>
          </div>

          <div className="max-h-[min(65vh,32rem)] overflow-y-auto overscroll-contain p-2" data-testid="list-get-started-items">
            {SETUP_ITEMS.map((item) => {
              const done = items[item.key];
              return (
                <Link
                  key={item.key}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="flex items-start gap-2.5 rounded-lg px-2 py-2 hover:bg-muted transition-colors"
                  data-testid={`item-get-started-${item.key}`}
                >
                  {done ? (
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" data-testid={`check-done-${item.key}`} />
                  ) : (
                    <Circle size={16} className="text-muted-foreground/40 mt-0.5 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm font-medium ${done ? "text-muted-foreground line-through decoration-muted-foreground/40" : ""}`}>
                      {item.label}
                    </span>
                    {!done && <span className="block text-xs text-muted-foreground">{item.detail}</span>}
                  </span>
                  <ChevronRight size={14} className="text-muted-foreground/50 mt-1 shrink-0" />
                </Link>
              );
            })}

            {exploreLinks.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-2 pt-3 pb-1" data-testid="section-get-started-explore">
                  <Compass size={12} className="text-muted-foreground shrink-0" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Explore your plan
                  </span>
                </div>
                {exploreLinks.map((item) => (
                  <Link
                    key={item.key}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-2.5 rounded-lg px-2 py-2 hover:bg-muted transition-colors"
                    data-testid={`item-get-started-${item.key}`}
                  >
                    {item.done !== undefined ? (
                      item.done ? (
                        <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                      ) : (
                        <Circle size={16} className="text-muted-foreground/40 mt-0.5 shrink-0" />
                      )
                    ) : (
                      <Compass size={16} className="text-brand/60 mt-0.5 shrink-0" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{item.label}</span>
                      <span className="block text-xs text-muted-foreground">{item.detail}</span>
                    </span>
                    <ChevronRight size={14} className="text-muted-foreground/50 mt-1 shrink-0" />
                  </Link>
                ))}
              </>
            )}
          </div>
      </PopoverContent>
    </Popover>
  );
}
